/**
 * Tool calls as sentences (D120), DOM-free.
 *
 * A run of tool calls reads as what was done — "Read 3 files and ran 2
 * commands" — rather than a count. Commands and edits are named before
 * reads, at most two kinds are named, and the rest are "N other actions". A
 * group of nothing but thinking reads "Thought"; one call reads as itself,
 * in the past tense once it has finished ("Ran npm test", "Edited App.tsx")
 * and in the present while it runs ("Running npm test").
 *
 * The same words serve Chat, Research and Team Work member turns
 * (`chatPartsModel.toolGroupView`, `turnFoldModel`). Pinned by
 * `webview-ui/test/toolSummaryModel.test.ts`.
 *
 * Copied from the workbench's webview-ui/src/facilitator/toolSummaryModel.ts: the words read from this package's mirror
 * of shared/protocol/toolWords.ts, and the activity's type is the slice of `OpsActivity` they use.
 */
import { classifyActivity, doingOfStep, toolTarget } from '../protocol/toolWords.js';
export { doingOfStep, toolTarget };
/**
 * Whether an activity is a tool call. A Team Work member's call (and a Chat
 * call from round 0.43 on) is marked `kind: 'tool'`; a Chat reply saved
 * before that carries its calls as bare titles with no agent, kind or
 * detail, which in Chat only a tool call ever was.
 */
export function isToolActivity(s, mode) {
    if (s.kind === 'tool')
        return true;
    return mode === 'chat' && !s.agent && !s.kind && !s.detail;
}
/** The call's title and its ACP kind, wherever the host put them. */
function titleAndKind(s) {
    // Marked calls carry the ACP kind as the label and the title as the detail.
    if (s.kind === 'tool')
        return { title: (s.detail ?? '').trim() || s.label, acpKind: s.label };
    return { title: s.label };
}
/** One activity as a tool step. */
export function toolStepOf(s) {
    const { title, acpKind } = titleAndKind(s);
    const info = classifyActivity(acpKind, title);
    return { kind: info.kind, target: toolTarget(info.kind, title), title, status: s.status, verb: info.verb, doing: info.doing };
}
const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
/**
 * One call in words: "Running npm test" while it runs, "Ran npm test" once
 * it has finished; "Read App.tsx", "Edited App.tsx", "Fetched example.org".
 */
export function toolPhrase(step, live) {
    const t = step.target;
    switch (step.kind) {
        case 'shell':
            return live ? (t ? `Running ${t}` : 'Running a command') : t ? `Ran ${t}` : 'Ran a command';
        case 'read':
            return live ? (t ? `Reading ${t}` : 'Reading') : t ? `Read ${t}` : 'Read a file';
        case 'edit':
            return live ? (t ? `Editing ${t}` : 'Editing') : t ? `Edited ${t}` : 'Edited a file';
        case 'web':
            return live ? (t ? `Fetching ${t}` : 'Fetching a page') : t ? `Fetched ${t}` : 'Fetched a page';
        case 'think':
            return live ? 'Thinking' : 'Thought';
        case 'other':
            return live ? (t ? `Using ${t}` : 'Working') : t ? `Used ${t}` : 'Used a tool';
        default:
            return capitalise(live ? step.doing : step.verb);
    }
}
/** Kinds named first: what changed something (commands, edits), then what looked, then the rest. */
const PRIORITY = { shell: 0, edit: 0, read: 1, web: 1, memory: 1, board: 1, agents: 1, other: 2, think: 2 };
function times(n) {
    return n === 2 ? 'twice' : `${n} times`;
}
function kindLabel(kind, steps) {
    const n = steps.length;
    // Files are counted once each, however many times they were read or edited.
    const files = new Set(steps.map((s) => s.target ?? `#${s.title}`)).size;
    switch (kind) {
        case 'shell':
            return n === 1 ? 'Ran a command' : `Ran ${n} commands`;
        case 'edit':
            return files === 1 ? 'Edited a file' : `Edited ${files} files`;
        case 'read':
            return files === 1 ? 'Read a file' : `Read ${files} files`;
        case 'web':
            return n === 1 ? 'Fetched a page' : `Fetched ${n} pages`;
        case 'memory':
            return n === 1 ? 'Checked memory' : `Checked memory ${times(n)}`;
        case 'board':
            return n === 1 ? 'Updated the task board' : `Updated the task board ${times(n)}`;
        case 'agents':
            return n === 1 ? capitalise(steps[0].verb) : `Messaged teammates ${times(n)}`;
        case 'think':
            return 'Thought';
        default:
            return n === 1 ? 'Used a tool' : `Used ${n} tools`;
    }
}
/**
 * A run of calls as one sentence. One call reads as itself; nothing but
 * thinking reads "Thought"; otherwise at most two kinds are named —
 * commands and edits first — and the rest are counted as other actions.
 */
export function toolSentence(steps) {
    if (!steps.length)
        return '';
    if (steps.length === 1)
        return toolPhrase(steps[0], false);
    const working = steps.filter((s) => s.kind !== 'think');
    if (!working.length)
        return 'Thought';
    if (working.length === 1)
        return toolPhrase(working[0], false);
    const byKind = new Map();
    working.forEach((s, i) => {
        const g = byKind.get(s.kind);
        if (g)
            g.steps.push(s);
        else
            byKind.set(s.kind, { first: i, steps: [s] });
    });
    const named = [...byKind]
        .sort(([a, ga], [b, gb]) => PRIORITY[a] - PRIORITY[b] || ga.first - gb.first)
        .slice(0, 2);
    const labels = named.map(([kind, g], i) => {
        const label = kindLabel(kind, g.steps);
        return i === 0 ? label : label.charAt(0).toLowerCase() + label.slice(1);
    });
    const rest = working.length - named.reduce((n, [, g]) => n + g.steps.length, 0);
    if (rest > 0)
        labels.push(`${rest} other action${rest === 1 ? '' : 's'}`);
    return labels.length < 3 ? labels.join(' and ') : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
/**
 * A tool call event as one line: its title, then the command it ran or what it acted on, which the host puts on
 * the event (`command`, `input`) because the title is often only the tool's name ("Terminal", "Fetch"). Mirrors
 * the host's src/facilitator/toolCallText.ts, for the views that read raw events (Mission Control, Agent history).
 */
export function toolCallLine(data) {
    const title = typeof data.title === 'string' ? data.title.trim() : '';
    const command = typeof data.command === 'string' ? data.command.trim().split('\n')[0] : '';
    const input = typeof data.input === 'string' ? data.input.trim() : '';
    const clipTo = (t, n) => (t.length > n ? `${t.slice(0, n - 1)}…` : t);
    if (command && !title.includes(command.slice(0, 24)))
        return `${title || 'Terminal'}: ${clipTo(command, 300)}`;
    if (input && !title.includes(input.slice(0, 30)))
        return `${title || 'Tool'} ${clipTo(input, 300)}`;
    return title;
}
//# sourceMappingURL=toolSummaryModel.js.map